import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit } from "@angular/core";
import { BannerType } from "./banner-type.enum";
import { Banner } from "./banner.model";
import { BannerService } from "./banner.service";

@Component({
  selector: "platform-banner",
  templateUrl: "./banner.component.html",
  styleUrls: ["./banner.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent implements OnInit, OnDestroy {
  // CSS variable the platform layout reads to offset its header/nav/content
  // down by the banner's height. It is set to 0 whenever no banner is shown,
  // so the space is reclaimed (the header moves back up) on dismiss.
  private static readonly heightVar = "--release-banner-height";

  banners: Banner[] = [];
  private lastPublishedHeight = -1;

  // Only the top banner is shown at a time; the next surfaces when it is dismissed.
  get topBanner(): Banner {
    return this.banners[0];
  }

  constructor(
    private readonly bannerService: BannerService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly elementRef: ElementRef<HTMLElement>
  ) { }

  ngOnInit(): void {
    this.bannerService.getActiveBanners().subscribe(banners => {
      this.banners = banners.filter(banner => !this.bannerService.isDismissed(banner.id));
      this.changeDetector.markForCheck();
      this.updateOffsetAfterRender();
    });
  }

  @HostListener("window:resize")
  onResize(): void {
    this.updateOffsetAfterRender();
  }

  ngOnDestroy(): void {
    this.setBannerHeight(0);
  }

  dismiss(banner: Banner): void {
    this.bannerService.dismiss(banner.id);
    this.banners = this.banners.filter(b => b.id !== banner.id);
    this.changeDetector.markForCheck();
    this.updateOffsetAfterRender();
  }

  iconName(type: BannerType): string {
    switch (type) {
      case BannerType.Success:
        return "check-circle";
      case BannerType.Warning:
        return "exclamation-triangle";
      case BannerType.Failure:
        return "times-circle";
      default:
        return "info-circle";
    }
  }

  cssClass(type: BannerType): string {
    return `banner--${(type || BannerType.Info).toLowerCase()}`;
  }

  // Measure after the DOM has updated and laid out, so the offset always matches
  // the actual banner (including multi-line wrapping) or 0 when there is none.
  private updateOffsetAfterRender(): void {
    requestAnimationFrame(() => {
      const stack = this.elementRef.nativeElement.querySelector(".banner-stack") as HTMLElement;
      this.setBannerHeight(stack ? stack.offsetHeight : 0);
    });
  }

  private setBannerHeight(height: number): void {
    if (height === this.lastPublishedHeight) {
      return;
    }
    this.lastPublishedHeight = height;
    document.documentElement.style.setProperty(BannerComponent.heightVar, `${height}px`);
  }
}
